/**
 * THE CAPTURE HARNESS — a dev-only door for repeatable visual A/B.
 *
 *     npm run dev, then open  ?cap=ground   ?cap=camp   ?cap=far   ?cap=sward
 *
 * `CLAUDE.md` requires a screenshot at 200–400 m over a camp at midday before
 * any visual claim, and `PLAYABLE.md` asks for a playtest harness that can
 * launch a fresh world from a stable seed. Both need the same thing: the
 * ability to put the camera in *exactly* the same place, at *exactly* the same
 * instant, twice — once before a change and once after.
 *
 * Why this is a module and not a console snippet. It was a console snippet
 * first, and it could not work: the preview pane reloads the page between
 * evaluations, so nothing installed on `window` survives to the screenshot,
 * and `requestAnimationFrame` does not run at all while the pane is hidden —
 * so a snippet that pumps frames deadlocks and a snippet that parks the camera
 * is erased before anything is drawn. Reading the station out of the URL
 * sidesteps both: the reload *is* the mechanism, because the query survives it.
 *
 * What it pins, and why each one is load-bearing:
 *
 *  · **An ABSOLUTE `clock.t`, not a time of day.** Weather drifts by *day*.
 *    Two shots at "noon" on different days are lit the same and *weathered*
 *    differently, and the difference reads as a rendering change that is not
 *    there.
 *  · **The world does not advance.** The station reuses the `crossing` flag
 *    that an absence already uses to own the clock, so `tPrev === clock.t` and
 *    `sim.advance` is a no-op. A capture writes no history — law 9, and the
 *    practical half of it: a shot that advanced the world could not be a
 *    control for the next one.
 *  · **The presence is re-parked every frame**, after `presence.step`, because
 *    `step` integrates velocity and drift and would otherwise walk the station
 *    out from under a long capture.
 *
 * It is dev-only in the strict sense — `main.ts` only calls this inside
 * `import.meta.env.DEV`, so it is not in a production bundle, and it can never
 * become player-facing UI (`DECISIONS.md`, on the time door).
 */

/** Where the camera stands, and what it looks at. */
export interface Station {
  from: { x: number; y: number; z: number };
  at: { x: number; y: number; z: number };
}

/**
 * One fixed instant: day 36500, `tod` 0.52.
 *
 * 0.52 is the midday the gate names, and the sun maths in `main.ts` agrees —
 * `elv = 1.05·sin((tod − 0.25)·2π)` puts the sun 59.7° up. Night is near-black
 * and every shadow claim would be unreadable there.
 */
export const CAPTURE_T = 36500 * 86400 + 0.52 * 86400;   // 3153644928

/**
 * `?tod=0.70` — the same pinned DAY at another hour, for lighting work.
 *
 * The gate's midday is a 60° sun, which is the flattest light the world's day
 * has to offer and the wrong instant to judge how the land takes light. Day
 * 36500 was scanned (`weatherAt` over the camp, in 0.02 steps): it is clear
 * from tod 0.44 to 0.54, under a full lid from 0.56 to 0.68, and clear again
 * at **0.70**, where the sun stands 18.6° up at azimuth 49° — a golden hour
 * that is deterministic in exactly the way the midday is. The day never
 * changes here, so the weather field is the same field; only the hour moves.
 * Out-of-range or absent → the gate's own 0.52.
 */
export function captureT(search: string): number {
  const raw = Number(new URLSearchParams(search).get('tod'));
  const tod = Number.isFinite(raw) && raw > 0 && raw < 1 ? raw : 0.52;
  return 36500 * 86400 + tod * 86400;
}

/**
 * `band-11` on the shipped island: 39 people, the camp every figure in this
 * session's before/after is measured over. Hard-coded on purpose — a station
 * that hunts for "the nearest band" is not the same station after the world
 * moves, and then the control is not a control.
 */
export const CAPTURE_CAMP = { x: -10757, z: 7753 };

type Ground = (x: number, z: number) => number;

/**
 * The stations. `camp` and `far` are the standing gate; `ground` and `sward`
 * are what this session is actually about — low and oblique, so the terrain
 * has to read as a *slope* and plant/ground contact is on screen at pixel
 * scale rather than implied from 300 m.
 */
export const STATIONS: Record<string, (g: Ground) => Station> = {
  camp: (g) => {
    const y = g(CAPTURE_CAMP.x, CAPTURE_CAMP.z);
    return { from: { x: CAPTURE_CAMP.x + 130, y: y + 300, z: CAPTURE_CAMP.z + 210 },
             at: { x: CAPTURE_CAMP.x, y, z: CAPTURE_CAMP.z } };
  },
  far: (g) => {
    const y = g(CAPTURE_CAMP.x, CAPTURE_CAMP.z);
    return { from: { x: CAPTURE_CAMP.x + 1300, y: y + 2400, z: CAPTURE_CAMP.z + 2100 },
             at: { x: CAPTURE_CAMP.x, y, z: CAPTURE_CAMP.z } };
  },
  ground: (g) => {
    const x = CAPTURE_CAMP.x + 150, z = CAPTURE_CAMP.z + 120, y = g(x, z);
    return { from: { x, y: y + 9, z: z + 30 },
             at: { x: x - 10, y: y + 0.8, z: z - 14 } };
  },
  sward: (g) => {
    const x = CAPTURE_CAMP.x - 300, z = CAPTURE_CAMP.z + 400, y = g(x, z);
    return { from: { x, y: y + 7, z: z + 22 },
             at: { x: x - 6, y: y + 0.5, z: z - 10 } };
  },
  /**
   * THE PLOT — steeply down over open, evenly vegetated ground away from the
   * camp's packed earth. This is the station that answers "is the footprint a
   * square", and it is deliberately not one of the two gate stations: the gate
   * asks whether the world looks right, this asks whether one shape is honest,
   * and a view chosen to make a shape legible is the wrong view for the first
   * question. `pitch` stops at 1.5 rad in `ui/presence.ts`, so 86° is as near
   * straight down as the presence can be asked to look.
   */
  plot: (g) => {
    const x = CAPTURE_CAMP.x + 150, z = CAPTURE_CAMP.z + 120, y = g(x, z);
    return { from: { x, y: y + 165, z: z + 12 },
             at: { x, y, z } };
  },
  /**
   * CLOSE — the close-range look station (2026-08-18). Oak meadow 750 m
   * south-west of the camp, on a gentle slope that faces the evening sun
   * (`tools/`-style probe over `surfaceHeight`: slope 0.11, aspect toward
   * azimuth 49°). The eye stands twelve metres up looking east along −12.6°,
   * so the frame is the concept's frame — litter and grass at pixel scale in
   * the foreground, broadleaf trees in the middle ground, a tree line on the
   * far ridge and the horizon in the top of the frame. With `?tod=0.70` the
   * 18.6° sun stands ahead-right, four degrees outside the right edge, so the
   * ground is raked and every tree has a lit side and a shadow side. This is
   * the station the close-range before/after pair is measured on. It is not
   * one of the two gate stations and does not replace them.
   */
  close: (g) => {
    const x = -11377, z = 7373, y = g(x, z) + 12;
    // yaw −π/2 (toward +x), pitch −0.22 rad
    return { from: { x, y, z },
             at: { x: x + 97.6, y: y - 21.8, z } };
  },
  /**
   * TREES and OAK — the hero-tree benchmark stations (2026-08-18). `trees`
   * stands five metres up, eighteen metres from a broadleaf in the oak meadow,
   * looking slightly up into its crown with the meadow's other trees behind;
   * `oak` stands six metres up, forty metres from the biggest spreading tree
   * near the close station, with a tall conifer beside it — the one frame that
   * holds all three near kinds. `&tod=0.70` for the raking light, default for
   * the midday lit/shade separation. The tree geometry before/after pairs are
   * measured on these.
   */
  trees: (g) => {
    const x = -11248, z = 7398, y = g(x, z) + 5;
    return { from: { x, y, z },
             at: { x: x - Math.sin(0.85) * 100 * Math.cos(0.12), y: y + Math.sin(0.12) * 100, z: z - Math.cos(0.85) * 100 * Math.cos(0.12) } };
  },
  oak: (g) => {
    const x = -10905, z = 7652, y = g(x, z) + 6;
    return { from: { x, y, z },
             at: { x: x - Math.sin(0.91) * 100 * Math.cos(0.12), y: y + Math.sin(0.12) * 100, z: z - Math.cos(0.91) * 100 * Math.cos(0.12) } };
  },
  /**
   * POPLAR — the river poplar stand at the big river's confluence, 4.4 km
   * east-south-east of the camp (2026-08-19): a dozen `columnar` trees of
   * 16–27 m on the bank, seen from twelve metres up and seventy metres off,
   * with the conifers and the riparian broadleafs behind. The columnar hero
   * pass is measured here.
   */
  poplar: (g) => {
    const x = -7822, z = 3300, y = g(x, z) + 12;
    return { from: { x, y, z },
             at: { x: x - Math.sin(0.75) * 100 * Math.cos(0.12), y: y + Math.sin(0.12) * 100, z: z - Math.cos(0.75) * 100 * Math.cos(0.12) } };
  },
  /**
   * RIVER — the Sweet Fork, where two rivers meet 5.5 km east-south-east of
   * the camp (2026-08-19, the river morphology pass): fourteen metres up on
   * the south bank, looking north-west up the confluence, with the big
   * river's bend and the tributary's mouth in one frame. `&tod=0.03` is the
   * night acceptance view, `&water=off` the dry-channel test.
   */
  river: (g) => {
    const x = -7760, z = 3240, y = g(x, z) + 14;
    return { from: { x, y, z },
             at: { x: x - Math.sin(0.9) * 100 * Math.cos(-0.18), y: y + Math.sin(-0.18) * 100, z: z - Math.cos(0.9) * 100 * Math.cos(-0.18) } };
  },
  /**
   * LOOK — a free station read out of the URL, dev-only like all of these:
   * `?cap=look&x=…&z=…&h=…&yaw=…&pitch=…` (h metres over the ground under
   * the eye; yaw 0 looks toward −z; pitch in radians, negative is down). It
   * exists so a composition can be searched for without editing this file
   * for every candidate; once found, the numbers get written down as a real
   * station above, because a station that lives only in a URL is not a
   * control anyone can reproduce.
   */
  look: (g) => {
    const q = new URLSearchParams(window.location.search);
    const num = (k: string, d: number) => { const v = Number(q.get(k)); return Number.isFinite(v) && q.get(k) !== null ? v : d; };
    const x = num('x', CAPTURE_CAMP.x + 190), z = num('z', CAPTURE_CAMP.z + 60);
    const y = g(x, z) + num('h', 14);
    const yaw = num('yaw', 0), pitch = num('pitch', -0.3);
    const cp = Math.cos(pitch);
    return { from: { x, y, z },
             at: { x: x - Math.sin(yaw) * cp * 100, y: y + Math.sin(pitch) * 100, z: z - Math.cos(yaw) * cp * 100 } };
  },
};

/** What the presence needs set to stand at a station. Yaw 0 looks toward −z. */
export function gazeTo(s: Station): { x: number; y: number; z: number; yaw: number; pitch: number } {
  const dx = s.at.x - s.from.x, dy = s.at.y - s.from.y, dz = s.at.z - s.from.z;
  return {
    x: s.from.x, y: s.from.y, z: s.from.z,
    yaw: Math.atan2(-dx, -dz),
    pitch: Math.atan2(dy, Math.max(1e-6, Math.hypot(dx, dz))),
  };
}

/** The station named in `?cap=…`, or null. Unknown names are ignored. */
export function requestedStation(search: string): string | null {
  const name = new URLSearchParams(search).get('cap');
  return name && name in STATIONS ? name : null;
}

/** Whether `?burn=1` asked for the scar rig below. */
export const burnRequested = (search: string): boolean =>
  new URLSearchParams(search).get('burn') === '1';

/** World-days asked for by `?days=N`, or 0. Clamped: this runs at boot. */
export function capDays(search: string): number {
  const raw = Number(new URLSearchParams(search).get('days'));
  return Number.isFinite(raw) ? Math.max(0, Math.min(2000, Math.floor(raw))) : 0;
}

/** Whether `?probe=1` asked for the framebuffer reading below. */
export const probeRequested = (search: string): boolean =>
  new URLSearchParams(search).get('probe') === '1';

/**
 * WHAT THE GROUND ACTUALLY LOOKS LIKE, IN PIXELS.
 *
 * "The terrain reads flat" is a claim about the screen, and the screen is the
 * only place it can be settled. A CPU-side probe over `groundColour` cannot:
 * the terrain material injects a per-material grain and a three-octave
 * fragment-normal relief in GLSL, so most of what the eye receives at close
 * range never exists on the CPU at all. Measuring the vertex pipeline and
 * concluding the screen is flat is exactly the mistake this exists to stop.
 *
 * Read INSIDE the frame, immediately after `render()` — a readback taken at
 * any other time returns black, because the drawing buffer is not preserved.
 *
 * Reports, over a centred box of the ground (the HUD is excluded by
 * construction — it is DOM, not canvas, so it is never in the buffer):
 *
 *  · `mean`, `sd`   — luminance and its spread, 0..255
 *  · `hp`           — HIGH-PASS sd: each pixel minus the mean of its 17×17
 *                     neighbourhood. This is the number that matters. Broad
 *                     shading gradients inflate plain `sd` while leaving the
 *                     surface looking like dough; `hp` sees only the detail
 *                     that makes ground read as a material.
 *  · `p01`, `p99`   — the working range, to catch a change that only moves
 *                     outliers.
 */
export function frameReading(
  gl: WebGL2RenderingContext | WebGLRenderingContext, w: number, h: number,
): { mean: number; sd: number; hp: number; p01: number; p99: number; n: number } {
  // a centred box: ground at every station, and clear of the verb bar
  const bw = Math.floor(w * 0.5), bh = Math.floor(h * 0.42);
  const x0 = Math.floor((w - bw) / 2), y0 = Math.floor(h * 0.30);
  const buf = new Uint8Array(bw * bh * 4);
  gl.readPixels(x0, y0, bw, bh, gl.RGBA, gl.UNSIGNED_BYTE, buf);

  const lum = new Float64Array(bw * bh);
  for (let i = 0; i < bw * bh; i++) {
    lum[i] = 0.2126 * buf[i * 4] + 0.7152 * buf[i * 4 + 1] + 0.0722 * buf[i * 4 + 2];
  }
  let sum = 0;
  for (let i = 0; i < lum.length; i++) sum += lum[i];
  const mean = sum / lum.length;
  let varAcc = 0;
  for (let i = 0; i < lum.length; i++) varAcc += (lum[i] - mean) ** 2;

  // high pass: subtract a box mean, which is what separates "this ground has a
  // surface" from "this hillside is lit"
  const R = 8;
  let hpAcc = 0, hpN = 0;
  for (let y = R; y < bh - R; y += 2) for (let x = R; x < bw - R; x += 2) {
    let loc = 0;
    for (let dy = -R; dy <= R; dy += 2) for (let dx = -R; dx <= R; dx += 2) {
      loc += lum[(y + dy) * bw + (x + dx)];
    }
    const k = (R + 1) * (R + 1);
    hpAcc += (lum[y * bw + x] - loc / k) ** 2;
    hpN++;
  }

  const sorted = Float64Array.from(lum).sort();
  return {
    mean: +mean.toFixed(2),
    sd: +Math.sqrt(varAcc / lum.length).toFixed(2),
    hp: +Math.sqrt(hpAcc / Math.max(1, hpN)).toFixed(3),
    p01: +sorted[Math.floor(sorted.length * 0.01)].toFixed(1),
    p99: +sorted[Math.floor(sorted.length * 0.99)].toFixed(1),
    n: lum.length,
  };
}

/** Whether `?bless=1` asked for the strengthen rig below. */
export const blessRequested = (search: string): boolean =>
  new URLSearchParams(search).get('bless') === '1';

/**
 * A FIXED BURN, for proving what a burn looks like.
 *
 * The question "is the burn a square" is a question about *rendering*, so the
 * honest control holds the burn itself constant and changes only the code that
 * draws it. That is what this is: a hard-coded polyomino of scar cells written
 * straight into the Overlay, identical on every run and on every build, so a
 * before/after pair differs in exactly one thing.
 *
 * The shape is what `sim/fire.ts` actually produces — `scarOut` writes whole
 * cells at the cell centre with the episode's severity, so a real fire IS an
 * axis-aligned polyomino of full cells. Writing one by hand is not a
 * simplification of the real thing; it is the real thing, minus the world-days
 * of waiting for it to spread.
 *
 * Offsets are in CELLS from the cell under the station's subject, and the
 * outline is deliberately ragged at the cell level so the test is "does the
 * *rendering* round this off", not "is a rectangle a rectangle".
 */
const BURN_CELLS: ReadonlyArray<readonly [number, number, number]> = [
  // [dx, dz, severity]
  [0, 0, 0.95], [1, 0, 0.92], [-1, 0, 0.88], [0, 1, 0.94], [0, -1, 0.86],
  [1, 1, 0.80], [-1, 1, 0.83], [1, -1, 0.72], [-1, -1, 0.68],
  [2, 0, 0.64], [2, 1, 0.58], [0, 2, 0.61], [1, 2, 0.55], [-2, 0, 0.52],
  [-1, 2, 0.44], [3, 0, 0.38], [2, -1, 0.41], [0, -2, 0.36],
];

/** Cells for `?burn=1`, in world coordinates, around a station's subject. */
export function burnRig(station: string, g: Ground): Array<{ x: number; z: number; sev: number }> {
  const s = STATIONS[station](g);
  const CELL = 32;
  const cx = Math.floor(s.at.x / CELL), cz = Math.floor(s.at.z / CELL);
  return BURN_CELLS.map(([dx, dz, sev]) => ({
    x: (cx + dx + 0.5) * CELL, z: (cz + dz + 0.5) * CELL, sev,
  }));
}

/**
 * A FIXED BLESSING, same logic. `?bless=1` writes biomass deviation directly
 * into the EnvField at the cell centres a close cast would have snapped to.
 *
 * `act()` snaps any cast under 32 m to its cell centre and stamps a 16 m
 * radius, which reaches no neighbouring cell centre — so this *is* what a
 * close Strengthen writes, one saturated cell, and it is why the answer looked
 * like a tile. Two cells, so the pair-boundary is on screen too.
 */
export function blessRig(station: string, g: Ground): Array<{ x: number; z: number; d: number }> {
  const s = STATIONS[station](g);
  const CELL = 32;
  const cx = Math.floor(s.at.x / CELL), cz = Math.floor(s.at.z / CELL);
  return [
    { x: (cx + 0.5) * CELL, z: (cz + 0.5) * CELL, d: 1 },
    { x: (cx + 1.5) * CELL, z: (cz + 0.5) * CELL, d: 0.75 },
  ];
}
